// src/legal/useCurrentTerms.js — the terms box's state (v221), for /set-password, the purchase and the plan-change confirmations (v222).
//
// ⭐ Read EVERY time `enabled` turns true (the form or the dialog opens) — never cached. `enabled` false → idle,
//    and any answer still in flight is dropped (sequence ticket: a stale answer or failure never lands).
// ⛔ A failed read BLOCKS (status 'failed', canProceed false) until «Riprova» succeeds: versions never read are
//    never sent (user decision 2026-11-03).
// ⭐ applyConflict(err): the 409 TERMS_VERSION_CHANGED body becomes the shown terms, the box is unticked and
//    `changed` is set (the host shows the amber notice).
import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchCurrentTerms } from './legalApi.js';
import { termsBody, termsFromConflict } from './termsModel.js';

const IDLE = { status: 'idle', terms: null, accepted: false, changed: false };

export default function useCurrentTerms(enabled) {
  const [state, setState] = useState(IDLE);
  const ticket = useRef(0);

  const load = useCallback(async () => {
    ticket.current += 1;
    const mine = ticket.current;
    setState({ status: 'loading', terms: null, accepted: false, changed: false });
    try {
      const terms = await fetchCurrentTerms();
      if (mine === ticket.current) setState({ status: 'ready', terms, accepted: false, changed: false });
    } catch {
      if (mine === ticket.current) setState({ status: 'failed', terms: null, accepted: false, changed: false });
    }
  }, []);

  useEffect(() => {
    if (enabled) {
      load();
      return undefined;
    }
    ticket.current += 1;   // drop any answer still in flight
    setState(IDLE);
    return undefined;
  }, [enabled, load]);

  const setAccepted = useCallback((accepted) => {
    setState((s) => (s.status === 'ready' ? { ...s, accepted: !!accepted } : s));
  }, []);

  const applyConflict = useCallback((err) => {
    ticket.current += 1;
    setState({ status: 'ready', terms: termsFromConflict(err), accepted: false, changed: true });
  }, []);

  return {
    ...state,
    canProceed: state.status === 'ready' && state.accepted,
    retry: load,
    setAccepted,
    applyConflict,
    body: () => termsBody(state.terms),
  };
}
